/* screens/sales/PublicPlansTree.jsx — дерево публичной линейки: B2C/B2B → тариф → сроки.

   Только вёрстка: данные (нормализованные publicPlansOf), состояния запроса и
   обработчики приходят пропсами. Так дерево проверяется рендером в строку
   (PublicPlans.test.jsx) без провайдеров, а запросы и дровер живут в PublicPlansPanel.

   В строке — то, что менеджер сверяет перед правкой: срок, цена, цена за месяц и
   скидка (С СЕРВЕРА — своей арифметики нет), лимиты, статус продажи по Москве,
   витрина, кто сидит на тарифе, прямой адрес /buy с «Копировать». */
import React from 'react';
import { EmptyState, ErrorState } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { fmt } from '../../lib/format.js';
import { chatText, saleStatus, seatsNb, termText, voiceText } from './publicPlans.js';

export const PUBLIC_UTM_HINT = 'Ссылки для продаж с учётом — в разделе «UTM-метки»';

// Адрес — только абсолютный http(s): строка приходит с сервера, но в href уходит
// без проверки ничего (S5 — `javascript:` в ссылке исполнился бы по клику). Тот же
// фильтр решает и про «Копировать»: продавец вставляет скопированное клиенту, и
// `javascript:…` или относительный `/buy/x` в буфере — это ссылка, которая у клиента
// либо опасна, либо никуда не ведёт.
export const safeHref = (url) => (typeof url === 'string' && /^https?:\/\/[^\s/]/i.test(url) ? url : null);

function priceText(plan) {
  if (!plan.is_paid && !plan.price_rub) return 'Бесплатно';
  return fmt.money(plan.price_rub);
}

/* ── Строка срока ── */
function TermRow({ plan, now, onEdit, onCopyPlan, onCopyLink }) {
  const st = saleStatus(plan, now);
  const ws = plan.workspaces_count;
  const showPerMonth = plan.months > 1 && plan.per_month_rub != null;
  const d = plan.discount_pct;
  const href = safeHref(plan.buy_url);
  return (
    <div className="sc-pub-row" data-slug={plan.slug}>
      <div className="sc-pub-main">
        <span className="sc-pub-main__name">{plan.name}</span>
        <span className="sc-pub-main__slug num">{plan.slug}</span>
        {plan.sales_note && <span className="sc-pub-main__note">{plan.sales_note}</span>}
      </div>

      <div className="sc-pub-cell" data-k="Срок">
        <span className="num">{termText(plan)}</span>
        <span className="sc-pub-sub">
          {plan.renewable ? 'с автопродлением' : 'разовый'}
          {plan.starts_after_current ? ' · после текущего' : ''}
        </span>
      </div>

      <div className="sc-pub-cell" data-k="Цена">
        <span className="num sc-pub-strong">{priceText(plan)}</span>
        {showPerMonth && <span className="sc-pub-sub num">{fmt.money(plan.per_month_rub)}/мес.</span>}
        {d > 0 && <span className="sc-pub-tagline"><Pill tone="ok" size="sm" glyph={false}>{fmt.percent(-d)}</Pill></span>}
        {/* Отрицательная скидка — срок дороже помесячной оплаты: ошибка в ценах,
            которую здесь же и исправляют, поэтому показываем, а не прячем. */}
        {d < 0 && (
          <span className="sc-pub-tagline">
            <Pill tone="warn" size="sm">дороже помесячной на {fmt.percent(Math.abs(d))}</Pill>
          </span>
        )}
      </div>

      <div className="sc-pub-cell" data-k="Лимиты">
        <span>запросы <span className="num">{chatText(plan.chat_messages_per_30d)}</span></span>
        <span className="sc-pub-sub">голос {voiceText(plan.voice_seconds_per_30d)}</span>
      </div>

      <div className="sc-pub-cell sc-pub-tags" data-k="Статус">
        <span className="sc-pub-status">
          <Pill tone={st.tone} size="sm">{st.label}</Pill>
          {st.note && <span className="sc-pub-sub">{st.note}</span>}
        </span>
        <Pill tone="neutral" size="sm" icon={plan.show_in_storefront ? 'eye' : 'eye-off'}>
          {plan.show_in_storefront ? 'в витрине' : 'только по ссылке'}
        </Pill>
        {plan.is_default && <Pill tone="accent" size="sm" glyph={false}>По умолчанию</Pill>}
      </div>

      <div className="sc-pub-cell" data-k="Пространства">
        <span className={ws ? 'sc-pub-strong' : ''}>{ws ? seatsNb(ws) : '—'}</span>
        {plan.auto_renew_count > 0 && <span className="sc-pub-sub">{plan.auto_renew_count} с автопродлением</span>}
        {plan.pinned_count > 0 && <span className="sc-pub-sub">{plan.pinned_count} по закреплённой цене</span>}
        {plan.queued_count > 0 && <span className="sc-pub-sub">+{plan.queued_count} в очереди</span>}
      </div>

      <div className="sc-pub-actions">
        <Button size="sm" variant="secondary" iconLeft="edit" onClick={() => onEdit(plan)}>Изменить</Button>
        <Button size="sm" variant="ghost" iconLeft="layers" onClick={() => onCopyPlan(plan)}>Скопировать</Button>
      </div>

      {plan.buy_url && (
        <div className="sc-pub-link">
          <span className="sc-pub-link__label">Прямой адрес, без учёта:</span>
          {href
            ? <a className="sc-pub-link__url num" href={href} target="_blank" rel="noopener noreferrer">{href}</a>
            : <span className="sc-pub-link__url num">{plan.buy_url}</span>}
          {href && (
            <Button size="sm" variant="ghost" iconLeft="copy" aria-label={`Копировать адрес ${plan.slug}`}
              onClick={() => onCopyLink(href, plan.slug)}>Копировать</Button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Семейство: тариф и его сроки ── */
function Family({ family, now, onNewTerm, ...rowProps }) {
  // «Новый срок» — только у платного обычного тарифа: срок у бесплатного демо и
  // у акции (у неё нет базы-семейства) смысла не имеет.
  const base = family.plans.find((p) => p.slug === family.key) || family.plans[0];
  const canAddTerm = !family.is_promo && !!(base && base.is_paid);
  const n = family.plans.length;
  return (
    <div className="sc-pub-family">
      <div className="sc-pub-family__head">
        <span className="sc-pub-family__title">
          <span className="sc-pub-family__name">{family.name}</span>
          {family.is_promo && <Pill tone="accent" size="sm" glyph={false}>Акция</Pill>}
          <span className="sc-pub-family__count t-caption">
            {n} {fmt.plural(n, 'срок', 'срока', 'сроков')}
          </span>
        </span>
        {canAddTerm && (
          <Button size="sm" variant="ghost" iconLeft="plus" onClick={() => onNewTerm(family.key)}>Новый срок</Button>
        )}
      </div>
      {family.plans.map((p) => <TermRow key={p.slug} plan={p} now={now} {...rowProps} />)}
    </div>
  );
}

export function PublicPlansTree({ groups, now, ...handlers }) {
  return groups.map((g) => {
    const count = g.families.reduce((s, f) => s + f.plans.length, 0);
    return (
      <section className="sc-pub-group" key={g.audience || g.label}>
        <h2 className="sc-plans-section__title t-h3">
          {g.label}<span className="sc-plans-section__count num">{count}</span>
        </h2>
        <div className="sc-pub-card">
          <div className="sc-pub-row sc-pub-row--head" aria-hidden="true">
            <span>Тариф</span><span>Срок</span><span>Цена</span><span>Лимиты</span>
            <span>Статус</span><span>Пространства</span><span />
          </div>
          {g.families.map((f) => <Family key={f.key} family={f} now={now} {...handlers} />)}
        </div>
      </section>
    );
  });
}

/* ── Тело вкладки со всеми состояниями ──
   data === null при loading=false — промежуточное состояние запроса: рисуем
   загрузку и не трогаем данные, которых ещё нет (S39). */
export function PublicPlansBody({ data, loading, error, now, onRetry, onNewPlan, onNewTerm, utmAction, ...rowHandlers }) {
  if (error) {
    return (
      <ErrorState title="Не удалось загрузить тарифы"
        body={(error && error.message) || 'Попробуйте ещё раз.'} onRetry={onRetry} />
    );
  }
  if (loading || !data) {
    return <div className="state state--loading"><Spinner size="lg" label="Загрузка тарифов…" /></div>;
  }
  const groups = data.groups || [];
  const canAddTerm = groups.some((g) => g.families.some((f) => !f.is_promo
    && f.plans.some((p) => p.slug === f.key && p.is_paid)));
  return (
    <div className="stack stack-16">
      <div className="sc-pub-hint" role="note">
        <Icon name="info" size={16} />
        <span>
          <b>{PUBLIC_UTM_HINT}</b>: там продавец берёт ссылку под свой канал, и переходы, регистрации
          и оплаты считаются по метке. Прямые адреса ниже — для проверки, без учёта.
        </span>
        {utmAction}
      </div>

      <div className="sc-plans-toolbar sc-pub-toolbar">
        <div className="grow" />
        {canAddTerm && (
          <Button variant="secondary" iconLeft="calendar" onClick={() => onNewTerm(null)}>Новый срок</Button>
        )}
        <Button variant="primary" iconLeft="plus" onClick={onNewPlan}>Новый тариф / акция</Button>
      </div>

      {groups.length === 0 ? (
        <EmptyState icon="tag" title="Публичных тарифов пока нет"
          body="Заведите тариф или акцию — новый тариф создаётся снятым с продажи и включается отдельно." />
      ) : (
        <PublicPlansTree groups={groups} now={now} onNewTerm={onNewTerm} {...rowHandlers} />
      )}
    </div>
  );
}
